import { useState } from "react";
import api from "@/lib/api";
import { formatApiError, inr, fmtDate } from "@/lib/utils";
import { X, Upload, ArrowRight, CheckCircle2, AlertCircle, Loader2, FileSpreadsheet } from "lucide-react";

const FIELD_LABELS = {
  // bookings
  status: "Status",
  client_name: "Client Name *",
  contact: "Contact",
  day1: "Day 1",
  day2: "Day 2",
  day3: "Day 3",
  day4: "Day 4",
  day5: "Day 5",
  couple_name: "Couple Name",
  location: "Location",
  event: "Event",
  reference: "Reference",
  last_updates: "Updates / Notes",
  management: "Management",
  // post-production
  event_date: "Event Date *",
  coordinator: "Coordinator",
  total_amount: "Total Amount (₹)",
  paid_amount: "Paid Amount (₹)",
  notes: "Notes",
};

const REQUIRED = {
  bookings: ["client_name"],
  postproduction: ["couple_name"],
};

export default function ImportMapper({ kind, open, onClose, onDone }) {
  const [step, setStep] = useState(1); // 1: upload, 2: review, 3: result
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  if (!open) return null;

  const reset = () => {
    setStep(1);
    setFile(null);
    setPreview(null);
    setMapping({});
    setErr("");
    setResult(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setErr("");
  };

  const doPreview = async () => {
    if (!file) {
      setErr("Please choose a file");
      return;
    }
    setLoading(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/import/${kind}/preview`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setMapping(data.suggested_mapping || {});
      setStep(2);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const doImport = async () => {
    setLoading(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("column_mapping", JSON.stringify(mapping));
      const { data } = await api.post(`/import/${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      setStep(3);
      onDone?.();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const fields = preview?.fields || [];
  const columns = preview?.columns || [];

  const isDateField = (f) => ["day1", "day2", "day3", "day4", "day5", "event_date"].includes(f);
  const isAmountField = (f) => ["total_amount", "paid_amount"].includes(f);

  const renderCell = (val, field) => {
    if (val === "" || val === null || val === undefined) return <span className="text-stone-300">—</span>;
    if (isDateField(field)) return <span className="font-mono text-xs">{fmtDate(val)}</span>;
    if (isAmountField(field)) return <span className="font-mono text-xs">{inr(val)}</span>;
    return <span className="text-xs">{String(val).slice(0, 40)}{String(val).length > 40 ? "…" : ""}</span>;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/40 fade-in" onClick={close}>
      <div className="bg-white w-full max-w-4xl h-full overflow-y-auto flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="import-mapper">
        {/* Header */}
        <div className="flex items-center justify-between px-6 h-16 border-b border-stone-200 shrink-0">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="w-5 h-5 text-rose-700" strokeWidth={1.6} />
            <div>
              <h2 className="font-heading text-lg font-semibold leading-tight">
                Import {kind === "bookings" ? "Bookings" : "Post-Production"}
              </h2>
              <div className="text-[11px] uppercase tracking-wider text-stone-500">
                Step {step} of 3 · {step === 1 ? "Upload file" : step === 2 ? "Review mapping" : "Done"}
              </div>
            </div>
          </div>
          <button className="btn-ghost p-2 rounded" onClick={close} data-testid="import-close">
            <X className="w-5 h-5" strokeWidth={1.6} />
          </button>
        </div>

        {/* Step 1: Upload */}
        {step === 1 && (
          <div className="flex-1 p-8 flex flex-col items-center justify-center">
            <div className="max-w-md w-full space-y-5">
              <div className="text-center">
                <Upload className="w-10 h-10 text-stone-400 mx-auto mb-3" strokeWidth={1.4} />
                <h3 className="font-heading text-xl font-semibold mb-2">Upload your spreadsheet</h3>
                <p className="text-sm text-stone-500">
                  CSV or Excel (.xlsx). Smart mapper will auto-detect your column names —
                  even if they&apos;re spelled differently like &ldquo;COUPLE NAME&rdquo;, &ldquo;RESION&rdquo;, or &ldquo;Coordinet With&rdquo;.
                </p>
              </div>

              <label className="block border-2 border-dashed border-stone-200 rounded-xl p-8 text-center cursor-pointer hover:border-rose-300 hover:bg-rose-50/30 transition-colors" data-testid="import-dropzone">
                <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleFile} data-testid="import-file-input" />
                {file ? (
                  <div>
                    <FileSpreadsheet className="w-7 h-7 text-rose-700 mx-auto mb-2" strokeWidth={1.5} />
                    <div className="font-medium text-stone-900 text-sm">{file.name}</div>
                    <div className="text-xs text-stone-500 mt-1">{(file.size / 1024).toFixed(1)} KB · Click to change</div>
                  </div>
                ) : (
                  <div>
                    <div className="text-sm font-medium text-stone-700">Click to choose a file</div>
                    <div className="text-xs text-stone-500 mt-1">.csv .xlsx .xls</div>
                  </div>
                )}
              </label>

              <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 text-xs text-stone-600">
                <div className="font-semibold text-stone-800 mb-1">Auto-handled formats:</div>
                <ul className="space-y-1 text-stone-600">
                  <li>• Dates: <span className="font-mono">5/5/2024</span>, <span className="font-mono">11-30-2025</span>, <span className="font-mono">5 May 2024</span>, Excel serial numbers</li>
                  <li>• Amounts: <span className="font-mono">INR 160,000</span>, <span className="font-mono">₹2,30,000</span>, <span className="font-mono">290000+40000</span> (sum)</li>
                </ul>
              </div>

              {err && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {err}
                </div>
              )}

              <button className="btn btn-primary w-full" onClick={doPreview} disabled={!file || loading} data-testid="import-preview-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" strokeWidth={1.6} />}
                Analyze file
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Review */}
        {step === 2 && preview && (
          <div className="flex-1 flex flex-col min-h-0">
            <div className="px-6 py-4 border-b border-stone-200 bg-stone-50/60 shrink-0">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="text-sm font-medium text-stone-900">
                    Detected <span className="font-semibold">{preview.total_rows}</span> rows ·{" "}
                    <span className="font-semibold">{Object.keys(mapping).filter((k) => mapping[k]).length}</span> of {fields.length} fields mapped
                  </div>
                  <div className="text-xs text-stone-500 mt-0.5">Review the mapping below. Unmatched columns will be ignored.</div>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Mapping table */}
              <div>
                <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-stone-700 mb-3">Column mapping</h3>
                <div className="card overflow-hidden">
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>App field</th>
                        <th>Your column</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fields.map((f) => {
                        const required = REQUIRED[kind]?.includes(f);
                        return (
                          <tr key={f}>
                            <td className="font-medium text-stone-800 w-1/3">
                              {FIELD_LABELS[f] || f}
                              {required && <span className="text-red-600">*</span>}
                            </td>
                            <td>
                              <select
                                className="input"
                                value={mapping[f] || ""}
                                onChange={(e) => setMapping({ ...mapping, [f]: e.target.value })}
                                data-testid={`map-${f}`}
                              >
                                <option value="">— Ignore —</option>
                                {columns.map((c) => (
                                  <option key={c} value={c}>{c}</option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Sample preview */}
              <div>
                <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-stone-700 mb-3">
                  Sample rows ({preview.sample_rows.length} of {preview.total_rows})
                </h3>
                <div className="card overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="tbl">
                      <thead>
                        <tr>
                          {fields.map((f) => (
                            <th key={f}>{FIELD_LABELS[f] || f}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.sample_rows.map((row, i) => {
                          const rowKey = `${row.couple_name || row.client_name || "row"}-${row.event_date || row.day1 || i}`;
                          return (
                            <tr key={rowKey}>
                              {fields.map((f) => (
                                <td key={f}>{renderCell(row[f], f)}</td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {err && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {err}
                </div>
              )}
            </div>

            <div className="border-t border-stone-200 px-6 py-4 flex justify-between bg-white shrink-0">
              <button className="btn btn-outline" onClick={() => setStep(1)} disabled={loading}>
                Back
              </button>
              <button className="btn btn-primary" onClick={doImport} disabled={loading} data-testid="import-confirm-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" strokeWidth={1.6} />}
                Import {preview.total_rows} rows
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Result */}
        {step === 3 && result && (
          <div className="flex-1 p-8 flex items-center justify-center">
            <div className="max-w-md w-full text-center space-y-5">
              <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto" strokeWidth={1.3} />
              <div>
                <h3 className="font-heading text-2xl font-semibold mb-2">Import complete</h3>
                <div className="text-sm text-stone-600">
                  <span className="font-semibold text-green-700">{result.inserted}</span> entries added
                  {result.skipped > 0 && (
                    <> · <span className="font-semibold text-amber-700">{result.skipped}</span> skipped (missing required fields)</>
                  )}
                </div>
              </div>

              {result.errors && result.errors.length > 0 && (
                <div className="text-left bg-amber-50 border border-amber-200 rounded-md p-3 text-xs">
                  <div className="font-semibold text-amber-900 mb-1">Some rows had errors:</div>
                  <ul className="space-y-1 max-h-32 overflow-y-auto">
                    {result.errors.slice(0, 5).map((e) => (
                      <li key={`err-${e.row}`} className="text-amber-800">Row {e.row}: {e.error}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex gap-2 justify-center pt-2">
                <button className="btn btn-outline" onClick={reset} data-testid="import-another-btn">Import another</button>
                <button className="btn btn-primary" onClick={close} data-testid="import-done-btn">Done</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
